import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Barracuda.css";
import barracudaLogo from "../../assets/images/partners/Barracuda.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const threatRows = [
  { icon: "📧", text: "Phishing / Spear Phishing", count: "14,287", status: "blocked" },
  { icon: "🦠", text: "Ransomware Payload", count: "2,041", status: "blocked" },
  { icon: "📤", text: "BEC / Executive Impersonation", count: "876", status: "blocked" },
  { icon: "🔗", text: "Malicious URL Click (rewritten)", count: "5,112", status: "blocked" },
  { icon: "💾", text: "Cloud Backup — SLA Met", count: "99.97%", status: "ok" },
  { icon: "🛡️", text: "WAF — App Threats Blocked", count: "18,400+", status: "blocked" },
];

const emailThreats = [
  { icon: "🎣", name: "Phishing & Spear Phishing", desc: "AI/ML detection blocks sophisticated phishing and spear phishing campaigns — including targeted attacks on executives, finance teams, and HR using personalized content and lookalike domains." },
  { icon: "💼", name: "Business Email Compromise", desc: "BEC attacks impersonating executives, vendors, or financial institutions requesting fraudulent wire transfers or credential disclosure are detected using behavioral and linguistic analysis." },
  { icon: "🦠", name: "Ransomware & Malware", desc: "Email-borne ransomware and malware is blocked before delivery using static analysis, behavioral sandboxing, and threat intelligence — stopping payload delivery at the email gateway." },
  { icon: "🔗", name: "Malicious Links & URLs", desc: "All links in emails are rewritten to route through Barracuda's URL filtering service — blocking access to malicious destinations at click time, even when links change from safe to malicious after delivery." },
  { icon: "📋", name: "Account Takeover Protection", desc: "Barracuda Sentinel uses AI to detect compromised mailbox behavior — identifying account takeover activity from unusual login locations, forwarding rules, and atypical email sending patterns." },
];

const backupTiers = [
  { icon: "🖥️", name: "Physical Server Backup", desc: "Agent-based bare-metal backup for Windows and Linux servers — with granular file/folder restore and full bare-metal recovery to dissimilar hardware using LiveBoot local virtualization." },
  { icon: "🔄", name: "VMware & Hyper-V Backup", desc: "Agentless backup of VMware vSphere and Microsoft Hyper-V virtual machines — with instant local recovery via VM LiveBoot and cloud seeding for off-site replicated copies." },
  { icon: "☁️", name: "SaaS Data Protection", desc: "Microsoft 365 and Google Workspace backup protecting mailboxes, SharePoint, OneDrive, Teams, and Google Drive — with granular item-level restore and point-in-time recovery for every user." },
  { icon: "🏗️", name: "Immutable Cloud Storage", desc: "Backup copies stored in Barracuda's WORM-compliant cloud storage — ensuring ransomware cannot encrypt or delete backup data, enabling guaranteed recovery even after a devastating ransomware attack." },
];

const cloudCards = [
  { icon: "🔐", title: "Cloud-to-Cloud Backup", desc: "Protect SaaS application data — Microsoft 365, Google Workspace, Salesforce — with automated daily backups and granular point-in-time restore for any user object including deleted items." },
  { icon: "🛡️", title: "Cloud Application Security", desc: "Barracuda Cloud Application Protection secures web applications and APIs deployed in AWS, Azure, and GCP — WAF protection, bot management, and DDoS mitigation for cloud-native workloads." },
  { icon: "🤖", title: "AI-Powered Threat Detection", desc: "Barracuda's AI/ML threat detection engine analyzes behavioral patterns, communication graphs, and linguistic signals — identifying threats that evade signature-based detection across email, web, and API traffic." },
  { icon: "🔑", title: "Cloud Security Posture", desc: "Identify and remediate cloud configuration vulnerabilities in AWS, Azure, and Google Cloud — detecting exposed S3 buckets, over-privileged IAM roles, unencrypted data stores, and non-compliant security group rules." },
  { icon: "🌐", title: "SASE & Zero Trust", desc: "Barracuda SecureEdge delivers cloud-native SD-WAN and Zero Trust Network Access — connecting branch offices, remote workers, and cloud workloads with consistent security policy without VPN infrastructure." },
  { icon: "📊", title: "Security Analytics", desc: "Centralized security analytics across email, web, network, and cloud — correlating events across the Barracuda platform to identify multi-stage attack chains and provide actionable threat intelligence." },
];

const networkCaps = [
  { icon: "🔥", title: "Next-Gen Firewall", desc: "Barracuda CloudGen Firewall with deep packet inspection, application awareness, and intrusion prevention — available as hardware appliances, virtual machines, or cloud-native deployments in AWS, Azure, and GCP." },
  { icon: "📡", title: "SD-WAN", desc: "Integrated SD-WAN connectivity providing intelligent traffic routing across MPLS, broadband, and LTE — with Barracuda CloudGen Firewall enforcing consistent security policy at every branch location." },
  { icon: "🔒", title: "Zero Trust Network Access", desc: "Barracuda CloudGen Access replaces VPN with identity-aware Zero Trust access — verifying user, device, and context before granting application-level access to on-premises and cloud resources." },
  { icon: "🕵️", title: "Industrial IoT Security", desc: "Barracuda Industrial Security provides OT-aware network monitoring for industrial environments — detecting anomalous behavior in SCADA, PLC, and HMI communications without disrupting operations." },
];

const frameworks = [
  "NIST Cybersecurity Framework", "ISO 27001", "SOC 2 Type II", "HIPAA",
  "PCI DSS", "GDPR", "CCPA", "FINRA", "FERPA", "CMMC",
];

const continuityCards = [
  { icon: "⚡", title: "Instant Local Recovery", desc: "Barracuda LiveBoot virtualizes protected servers locally in seconds — enabling immediate business continuity while production systems are recovered or replaced after an outage." },
  { icon: "☁️", title: "Cloud Failover", desc: "CloudLiveBoot replicates protected workloads to Barracuda Cloud — enabling cloud-hosted operation of production servers from any internet-connected location during datacenter outages." },
  { icon: "🔄", title: "Ransomware Recovery Guarantee", desc: "Barracuda provides a ransomware recovery guarantee for organizations with Barracuda Backup — committing to data recovery after a ransomware attack, backed by immutable cloud storage." },
  { icon: "📊", title: "DR Testing & Documentation", desc: "ALG Data Guard performs scheduled DR test recoveries — verifying backup integrity, documenting recovery time objectives, and providing evidence reports for compliance and cyber insurance requirements." },
];

const industryCards = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant email security, EHR backup, and PHI protection — securing patient data, telehealth platforms, and clinical staff remote access against healthcare-targeted ransomware." },
  { icon: "🏫", name: "Education", desc: "Email protection against student data phishing, ransomware-resilient backup for student records and research, and web filtering for K-12 and higher education networks." },
  { icon: "🏦", name: "Financial Services", desc: "Email security and BEC protection for financial institutions, compliance-aligned backup with immutable storage, and network security for trading systems and customer portals." },
  { icon: "⚖️", name: "Legal & Professional", desc: "Client communication protection against phishing and BEC, attorney-client privilege email archiving, and ransomware-resilient case file backup with granular restore." },
  { icon: "🏛️", name: "Government", desc: "Email security and web filtering for local and state government, NIST-aligned backup and disaster recovery, and network security for citizen-facing applications and internal systems." },
  { icon: "🏭", name: "Manufacturing", desc: "Industrial IoT security monitoring, OT-aware network segmentation, and business continuity for production systems — protecting manufacturing operations against ransomware disruption." },
  { icon: "🛒", name: "Retail & eCommerce", desc: "PCI DSS-aligned email security, web application protection for customer-facing systems, and SaaS data backup for retail applications running in Microsoft 365 and cloud platforms." },
  { icon: "💊", name: "Life Sciences", desc: "Research data protection with immutable backup, email security for clinical communication, and SOC 2-aligned cloud security for clinical trial management and pharmaceutical research platforms." },
];

const certCards = [
  { icon: "🥇", name: "Barracuda MSP Partner", desc: "Elite MSP partnership with Barracuda Networks — providing managed security services to customers using Barracuda's full platform, backed by Barracuda's technical and commercial support." },
  { icon: "🔐", name: "Email Security Specialist", desc: "Barracuda Email Security Gateway and Sentinel certified engineers providing advanced phishing protection, BEC detection, and email continuity deployments with full policy management." },
  { icon: "💾", name: "Backup & DR Certified", desc: "Certified in Barracuda Backup deployment, cloud replication configuration, LiveBoot recovery procedures, and DR testing methodology for VMware, Hyper-V, and SaaS environments." },
  { icon: "🛡️", name: "CloudGen Firewall Expert", desc: "Expert-level CloudGen Firewall deployment — including SD-WAN configuration, Zero Trust policy design, and cloud deployment in AWS, Azure, and GCP environments." },
];

const services = [
  { title: "Security Assessment", body: "We evaluate your current security posture against the Barracuda platform — identifying email protection gaps, backup coverage issues, network security weaknesses, and cloud security misconfigurations that Barracuda solutions address." },
  { title: "Email Security Deployment", body: "Expert deployment of Barracuda Email Security Gateway and Sentinel — configuring spam and phishing policies, sandboxing, link rewriting, DMARC enforcement, and Microsoft 365 / Google Workspace integration." },
  { title: "Backup & DR Implementation", body: "Complete Barracuda Backup deployment — agent installation, backup schedule configuration, cloud replication setup, LiveBoot testing, and DR procedure documentation with scheduled recovery testing." },
  { title: "WAF & Cloud Security", body: "Barracuda WAF-as-a-Service and Cloud Application Protection deployment — configuring OWASP protection, bot management, DDoS mitigation, and API security for cloud-hosted web applications and APIs." },
  { title: "CloudGen Firewall & SD-WAN", body: "Next-gen firewall deployment with SD-WAN — including hardware appliance installation, cloud deployment, policy configuration, intrusion prevention tuning, and Zero Trust ZTNA setup for remote access." },
  { title: "Managed XDR & SOC", body: "24/7 security monitoring using Barracuda's XDR platform — with ALG Data Guard analysts reviewing alerts, conducting threat hunting, and coordinating incident response across your entire Barracuda deployment." },
];

const faqData = [
  { q: "What is Barracuda Email Security Gateway vs. Barracuda Sentinel — and which does my organization need?", a: "Barracuda offers two complementary email security products that serve different threat vectors: Barracuda Email Security Gateway is the inbound/outbound email filtering gateway — it sits at the email perimeter, scanning all inbound email for spam, phishing, malware, and malicious links before delivery to user inboxes. It integrates as an MX record-level filter for on-premises Exchange or as a connector for Microsoft 365 and Google Workspace. This product blocks the majority of commodity and targeted attacks before they reach users. Barracuda Sentinel is an API-based email security product specifically for Microsoft 365 that uses AI to detect threats that have already been delivered to inboxes — particularly sophisticated BEC attacks, account takeover, and internal email threats that come from already-compromised internal accounts. Because Sentinel connects via Microsoft 365 API rather than email gateway, it can analyze historical email, detect compromised accounts, and flag anomalous behavior from internal senders. For maximum protection, ALG Data Guard recommends deploying both: Email Security Gateway blocks commodity and most targeted threats pre-delivery, while Sentinel provides AI-based detection of sophisticated BEC and account takeover threats that bypass traditional signature-based filtering." },
  { q: "Does Barracuda Backup protect Microsoft 365 and Google Workspace data?", a: "Yes — Barracuda Cloud-to-Cloud Backup provides comprehensive protection for SaaS data stored in Microsoft 365 and Google Workspace. Microsoft 365 coverage includes: Exchange Online mailboxes (email, calendar, contacts, tasks), SharePoint Online sites and document libraries, OneDrive for Business personal storage, Microsoft Teams messages, files, and tabs, and Microsoft 365 Groups data. Google Workspace coverage includes: Gmail mailboxes, Google Drive files and shared drives, Google Contacts, and Google Calendar. A common misconception is that Microsoft and Google are responsible for protecting SaaS data — they provide platform availability, but data protection (including accidental deletion, malicious deletion, ransomware affecting synced OneDrive/Drive data, and legal discovery) is the customer's responsibility. Barracuda's SaaS backup provides granular point-in-time restore for any item — individual emails, files, calendar events, and contacts — with unlimited retention in cloud storage. ALG Data Guard configures and manages Barracuda SaaS backup for customers — ensuring all users are enrolled, backup completions are monitored, and restore procedures are tested and documented." },
  { q: "What is Barracuda's ransomware recovery guarantee and how does it work?", a: "Barracuda's ransomware recovery guarantee is a program for Barracuda Backup customers that commits to data recovery following a ransomware attack. The guarantee is backed by Barracuda's immutable cloud storage — backup copies stored in Barracuda Cloud cannot be modified, encrypted, or deleted by ransomware, even if attackers gain administrative access to your backup infrastructure. How it works: Barracuda Backup creates local backup copies on the Barracuda appliance and replicates copies to Barracuda Cloud storage. Cloud copies are stored with WORM (Write Once Read Many) immutability — once written, they cannot be modified. If ransomware encrypts local data and local backup copies, cloud-stored immutable copies remain intact for recovery. Key conditions for the guarantee: the customer must have an active Barracuda Backup subscription with cloud replication enabled, the guaranteed data must have been backed up within the specified RPO, and recovery must follow Barracuda's documented recovery procedure. ALG Data Guard ensures customers qualify for the guarantee by configuring cloud replication, establishing appropriate backup schedules for the RPO, performing scheduled DR tests to validate recovery procedures, and documenting recovery runbooks that can be executed quickly following an attack." },
  { q: "What is Barracuda CloudGen Firewall and how does it compare to other next-gen firewalls?", a: "Barracuda CloudGen Firewall is a next-generation firewall with integrated SD-WAN capabilities — providing application-aware security policy, intrusion prevention, deep packet inspection, and intelligent traffic routing across multiple WAN connections. Key differentiators from traditional NGFW vendors: Integrated SD-WAN — CloudGen Firewall includes SD-WAN as a standard feature (not an add-on license), providing intelligent path selection across MPLS, broadband, and LTE connections with application-aware traffic steering and WAN optimization. Cloud-native deployment — CloudGen Firewall is available as public cloud-native (AWS, Azure, GCP) virtual appliances alongside hardware and virtual appliance form factors, with unified management across all deployment types. Centralized management at scale — Barracuda Firewall Control Center provides policy management and configuration deployment across hundreds of CloudGen Firewall instances with global change management — ideal for MSPs and enterprises with many branch locations. Barracuda does not charge per-seat for remote access VPN — CloudGen Firewall includes unlimited SSL VPN and ZTNA access seats with the platform license, unlike competitors charging per-concurrent-user VPN fees. ALG Data Guard deploys CloudGen Firewall for organizations replacing aging Cisco ASA, Fortinet FortiGate, or Meraki MX infrastructure — migrating policy, configuring SD-WAN, and deploying ZTNA remote access with zero-disruption cutover procedures." },
  { q: "How does ALG Data Guard's Barracuda managed service work?", a: "ALG Data Guard provides comprehensive managed Barracuda services covering the full platform lifecycle: Assessment & deployment: security posture assessment identifying Barracuda solution requirements, product procurement at Barracuda MSP pricing, and expert deployment including email gateway configuration, backup agent installation, firewall policy migration, and cloud security integration. Daily operations: email security policy management (allowlist/blocklist maintenance, phishing report review, quarantine management), backup monitoring (completion verification, alert response, and cloud replication health), and firewall policy change management. Incident response: when security events occur — whether email threats that bypassed filters, backup restoration needs, or firewall security events — ALG Data Guard responds with the Barracuda platform, providing investigation, containment, and remediation support. Compliance reporting: regular security reporting covering email threat volumes, backup completion rates, DR test results, and WAF traffic analysis — providing evidence documentation for compliance audits and cyber insurance renewals. Optimization: quarterly reviews of email security policy effectiveness, backup schedule optimization as data volumes grow, and firewall policy rationalization to remove outdated rules and update security posture as the threat landscape evolves." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Barracuda() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(13, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(200, 2200, metricsInView);
  const c4 = useCounter(24, 2000, metricsInView);

  return (
    <div className="bc-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={barracudaLogo} alt="Barracuda Networks" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Cybersecurity & Data Protection Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Barracuda Networks</h1>
            <p className="pd-identity-desc">
              Barracuda Networks delivers a comprehensive cybersecurity platform trusted by 200,000+ organizations worldwide — spanning email security, web application firewall, network security, data backup and disaster recovery, and cloud security. Barracuda's integrated platform protects organizations from the most common attack vectors: phishing emails, ransomware, application attacks, network intrusions, and cloud misconfigurations — delivering defense-in-depth from a single vendor without the complexity of assembling point products. ALG Data Guard is a certified Barracuda MSP partner providing deployment, management, and 24/7 monitoring services for the complete Barracuda security platform.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="bc-hero">
        <div className="bc-hero__scan-line" aria-hidden="true" />
        <div className="bc-hero__grid" aria-hidden="true" />
        <div className="bc-hero__glow" aria-hidden="true" />
        <div className="bc-hero__inner">
          <div>
            <div className="bc-hero__kicker">
              <span className="bc-hero__kicker-dot" />
              Barracuda Certified MSP Partner
            </div>
            <h2 className="bc-hero__title">
              Comprehensive Security.<br /><em>Simplified.</em>
            </h2>
            <p className="bc-hero__body">
              Barracuda's integrated security platform protects your organization from today's most sophisticated threats across email, web applications, network infrastructure, and cloud environments — replacing the complexity and cost of assembling multiple point security products with a unified platform managed by ALG Data Guard's security specialists.
            </p>
            <div className="bc-hero__actions">
              <button className="bc-btn bc-btn--blue" onClick={() => navigate("/contact")}>
                Get Security Assessment <ArrowIcon />
              </button>
              <button className="bc-btn bc-btn--ghost" onClick={() => navigate("/contact")}>
                View Platform Demo
              </button>
            </div>
            <div className="bc-hero__stats">
              <div><div className="bc-hero__stat-val">13B+</div><div className="bc-hero__stat-label">Emails scanned daily</div></div>
              <div><div className="bc-hero__stat-val">99.9%</div><div className="bc-hero__stat-label">Spam detection rate</div></div>
              <div><div className="bc-hero__stat-val">200K+</div><div className="bc-hero__stat-label">Organizations protected</div></div>
            </div>
          </div>

          {/* Threat intelligence panel */}
          <div className="bc-threat-panel">
            <div className="bc-threat-block">
              <div className="bc-threat-block__header">
                <span className="bc-threat-block__title">Threat Intelligence Dashboard</span>
                <span className="bc-status-badge bc-status-badge--ok">LIVE</span>
              </div>
              {threatRows.map(row => (
                <div key={row.text} className="bc-threat-row">
                  <span className="bc-threat-row__icon">{row.icon}</span>
                  <span className="bc-threat-row__text">{row.text}</span>
                  {row.status === "blocked"
                    ? <span className="bc-threat-row__count">{row.count}</span>
                    : <span className="bc-threat-row__safe">{row.count}</span>
                  }
                </div>
              ))}
              <div className="bc-protection-bar"><div className="bc-protection-fill" style={{ width:"94%" }} /></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Email Security ── */}
      <section className="bc-email">
        <div className="bc-shell">
          <div className="bc-email__inner">
            <img
              src="https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=900&q=80"
              alt="Barracuda email security"
              className="bc-email__img"
            />
            <div>
              <div className="bc-tag bc-tag--blue"><span className="bc-dot" /> Email Security</div>
              <h2 className="bc-email__title">Stop Threats Before They Reach the Inbox</h2>
              <p className="bc-email__body">
                Email is the primary attack vector for ransomware, business email compromise, and data theft — accounting for over 90% of successful cyberattacks. Barracuda Email Security Gateway provides multi-layer AI/ML protection blocking spam, phishing, malware, and BEC attacks before delivery, while Barracuda Sentinel uses behavioral AI to detect sophisticated threats and compromised accounts within Microsoft 365 mailboxes.
              </p>
              <p className="bc-email__body">ALG Data Guard deploys and manages Barracuda email security — handling policy configuration, quarantine management, DMARC enforcement, and ongoing tuning to maximize detection with minimal false positives.</p>
              <div className="bc-email__threats">
                {emailThreats.map(t => (
                  <div key={t.name} className="bc-threat-item">
                    <span className="bc-threat-item__icon">{t.icon}</span>
                    <div>
                      <div className="bc-threat-item__name">{t.name}</div>
                      <p className="bc-threat-item__desc">{t.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="bc-btn bc-btn--blue" onClick={() => navigate("/contact")}>
                  Deploy Email Security <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Backup & Recovery ── */}
      <section className="bc-backup">
        <div className="bc-shell">
          <div className="bc-backup__inner">
            <div>
              <div className="bc-tag bc-tag--dark"><span className="bc-dot" /> Backup & Disaster Recovery</div>
              <h2 className="bc-backup__title">Ransomware-Proof Backup. Instant Recovery.</h2>
              <p className="bc-backup__body">
                Barracuda Backup delivers comprehensive backup and disaster recovery for physical servers, virtual machines, SaaS applications, and databases — with immutable cloud storage ensuring backup data is protected against ransomware encryption and deletion. LiveBoot technology enables instant local server recovery in seconds, eliminating hours of downtime waiting for traditional restore operations.
              </p>
              <p className="bc-backup__body">
                ALG Data Guard manages Barracuda Backup environments end to end — configuring backup schedules, monitoring completion, managing cloud replication, and performing scheduled DR tests to validate recovery procedures. Organizations with Barracuda Backup and cloud replication qualify for Barracuda's ransomware recovery guarantee.
              </p>
              <div className="bc-backup__tiers">
                {backupTiers.map(tier => (
                  <div key={tier.name} className="bc-backup-tier">
                    <span className="bc-backup-tier__icon">{tier.icon}</span>
                    <div>
                      <div className="bc-backup-tier__name">{tier.name}</div>
                      <p className="bc-backup-tier__desc">{tier.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="bc-btn bc-btn--outline" style={{ color:"rgba(255,255,255,0.85)", borderColor:"rgba(255,255,255,0.3)" }} onClick={() => navigate("/contact")}>
                  Deploy Ransomware-Proof Backup <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
              alt="Barracuda backup and disaster recovery"
              className="bc-backup__img"
            />
          </div>
        </div>
      </section>

      {/* ── Cloud Security ── */}
      <section className="bc-cloud">
        <div className="bc-shell">
          <div className="bc-cloud__head">
            <div className="bc-tag bc-tag--blue"><span className="bc-dot" /> Cloud Security Platform</div>
            <h2 className="bc-title">Complete Cloud Security Across Every Attack Surface</h2>
            <p className="bc-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>Barracuda's cloud security portfolio protects SaaS data, cloud-hosted applications, network infrastructure, and cloud configurations — delivering consistent security across hybrid and multi-cloud environments.</p>
          </div>
          <div className="bc-cloud__grid">
            {cloudCards.map(card => (
              <div key={card.title} className="bc-cloud-card">
                <span className="bc-cloud-card__icon">{card.icon}</span>
                <h3 className="bc-cloud-card__title">{card.title}</h3>
                <p className="bc-cloud-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Network Defense ── */}
      <section className="bc-network">
        <div className="bc-shell">
          <div className="bc-network__inner">
            <div>
              <div className="bc-tag bc-tag--dark"><span className="bc-dot" /> Network Security</div>
              <h2 className="bc-network__title">Next-Gen Firewall with Integrated SD-WAN</h2>
              <p className="bc-network__body">
                Barracuda CloudGen Firewall delivers next-generation network security with application awareness, intrusion prevention, and deep packet inspection — available as hardware appliances, virtual machines, or cloud-native deployments in AWS, Azure, and GCP. Integrated SD-WAN intelligently routes traffic across MPLS, broadband, and LTE without separate SD-WAN appliances.
              </p>
              <p className="bc-network__body">
                ALG Data Guard deploys CloudGen Firewall for organizations requiring enterprise network security with SD-WAN connectivity — migrating from legacy Cisco, Meraki, or Fortinet platforms with policy translation, SD-WAN configuration, and zero-disruption network cutover.
              </p>
              <div className="bc-network__capabilities">
                {networkCaps.map(cap => (
                  <div key={cap.title} className="bc-net-cap">
                    <span className="bc-net-cap__icon">{cap.icon}</span>
                    <div>
                      <div className="bc-net-cap__title">{cap.title}</div>
                      <p className="bc-net-cap__desc">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="bc-btn bc-btn--outline" style={{ color:"rgba(255,255,255,0.85)", borderColor:"rgba(255,255,255,0.3)" }} onClick={() => navigate("/contact")}>
                  Replace Legacy Firewall <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=80"
              alt="Barracuda CloudGen Firewall and network security"
              className="bc-network__img"
            />
          </div>
        </div>
      </section>

      {/* ── Compliance ── */}
      <section className="bc-compliance">
        <div className="bc-shell">
          <div className="bc-compliance__inner">
            <div>
              <div className="bc-tag bc-tag--blue"><span className="bc-dot" /> Compliance & Regulatory</div>
              <h2 className="bc-compliance__title">Security Controls Mapped to Compliance Frameworks</h2>
              <p className="bc-compliance__body">
                Barracuda's security platform provides documented controls aligned with major compliance frameworks — enabling organizations to demonstrate security posture for audits, cyber insurance renewals, and regulatory examinations. ALG Data Guard provides compliance mapping documentation showing how Barracuda controls address specific framework requirements.
              </p>
              <p className="bc-compliance__body">
                From HIPAA healthcare security requirements through PCI DSS payment data protection to NIST Cybersecurity Framework controls — Barracuda's integrated platform addresses security requirements across email, web, network, and data protection domains in a single vendor relationship.
              </p>
              <div className="bc-compliance__frameworks">
                {frameworks.map(fw => (
                  <div key={fw} className="bc-framework">
                    <div className="bc-framework__check">✓</div>
                    {fw}
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="bc-btn bc-btn--blue" onClick={() => navigate("/contact")}>
                  Compliance Readiness Assessment <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=900&q=80"
              alt="Barracuda compliance security controls"
              className="bc-compliance__img"
            />
          </div>
        </div>
      </section>

      {/* ── Business Continuity ── */}
      <section className="bc-continuity">
        <div className="bc-shell">
          <div className="bc-continuity__head">
            <div className="bc-tag bc-tag--dark"><span className="bc-dot" /> Business Continuity</div>
            <h2 className="bc-title">Recover in Seconds. Not Days.</h2>
            <p className="bc-sub" style={{ maxWidth:"600px", margin:"0 auto" }}>Barracuda's business continuity capabilities ensure your organization can continue operating within minutes of a ransomware attack, hardware failure, or natural disaster — with instant recovery options from local appliance and cloud replicated copies.</p>
          </div>
          <div className="bc-continuity__grid">
            {continuityCards.map(card => (
              <div key={card.title} className="bc-cont-card">
                <span className="bc-cont-card__icon">{card.icon}</span>
                <h3 className="bc-cont-card__title">{card.title}</h3>
                <p className="bc-cont-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="bc-industries">
        <div className="bc-shell">
          <div className="bc-industries__head">
            <div className="bc-tag bc-tag--blue"><span className="bc-dot" /> Industries Served</div>
            <h2 className="bc-title">Protecting Organizations Across Every Sector</h2>
            <p className="bc-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Barracuda's security platform protects organizations across every industry vertical — with compliance-specific configurations and industry-tailored security policies.</p>
          </div>
          <div className="bc-industries__grid">
            {industryCards.map(ind => (
              <div key={ind.name} className="bc-industry-card">
                <span className="bc-industry-card__icon">{ind.icon}</span>
                <h3 className="bc-industry-card__name">{ind.name}</h3>
                <p className="bc-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="bc-metrics" ref={metricsRef}>
        <div className="bc-shell">
          <div className="bc-metrics__inner">
            <div><div className="bc-metric__val">{c1}B+</div><div className="bc-metric__label">Emails Scanned Daily</div></div>
            <div><div className="bc-metric__val">{c2}.9%</div><div className="bc-metric__label">Threat Detection Rate</div></div>
            <div><div className="bc-metric__val">{c3}K+</div><div className="bc-metric__label">Organizations Protected</div></div>
            <div><div className="bc-metric__val">{c4}/7</div><div className="bc-metric__label">SOC Monitoring</div></div>
          </div>
        </div>
      </section>

      {/* ── Our Partnership ── */}
      <section className="bc-compliance" style={{ paddingTop:"6rem", paddingBottom:"6rem" }}>
        <div className="bc-shell">
          <div className="bc-compliance__inner">
            <div>
              <div className="bc-tag bc-tag--blue"><span className="bc-dot" /> Our Partnership</div>
              <h2 className="bc-compliance__title">Barracuda Technology + ALG Data Guard Expertise</h2>
              <p className="bc-compliance__body">
                Security tools only deliver value when they're properly deployed, continuously tuned, and actively monitored. As a certified Barracuda MSP partner, ALG Data Guard provides the deployment expertise and managed services to realize the full potential of Barracuda's security platform — while you focus on running your business rather than managing security infrastructure.
              </p>
              <div className="bc-email__threats" style={{ marginTop:"1.5rem" }}>
                {services.map(s => (
                  <div key={s.title} className="bc-threat-item">
                    <span className="bc-threat-item__icon">✦</span>
                    <div>
                      <div className="bc-threat-item__name">{s.title}</div>
                      <p className="bc-threat-item__desc">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="bc-btn bc-btn--blue" onClick={() => navigate("/contact")}>
                  Start a Security Project <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=900&q=80"
              alt="ALG Data Guard Barracuda partnership"
              className="bc-compliance__img"
            />
          </div>
        </div>
      </section>

      {/* ── Certs ── */}
      <section className="bc-certs">
        <div className="bc-shell">
          <div className="bc-certs__head">
            <div className="bc-tag bc-tag--blue"><span className="bc-dot" /> Certifications</div>
            <h2 className="bc-title">Certified Barracuda MSP Partner</h2>
            <p className="bc-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>ALG Data Guard's Barracuda certifications ensure customers receive expert deployment and managed services backed by manufacturer support and technical training.</p>
          </div>
          <div className="bc-certs__grid">
            {certCards.map(cert => (
              <div key={cert.name} className="bc-cert-card">
                <span className="bc-cert-card__icon">{cert.icon}</span>
                <h3 className="bc-cert-card__name">{cert.name}</h3>
                <p className="bc-cert-card__desc">{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bc-faq">
        <div className="bc-shell">
          <div className="bc-faq__head">
            <div className="bc-tag bc-tag--blue" style={{ margin:"0 auto 1rem" }}><span className="bc-dot" /> FAQs</div>
            <h2 className="bc-title">Barracuda Security Questions, Answered</h2>
            <p className="bc-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Expert answers from ALG Data Guard's Barracuda-certified security engineers on email security, backup strategy, network defense, and managed services.</p>
          </div>
          <div className="bc-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`bc-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="bc-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="bc-faq-item__q">{faq.q}</span>
                  <span className="bc-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="bc-faq-item__answer"><div className="bc-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bc-cta">
        <div className="bc-cta__inner">
          <div className="bc-tag bc-tag--dark" style={{ margin:"0 auto 1.2rem" }}><span className="bc-dot" /> Get Protected</div>
          <h2 className="bc-cta__title">
            Comprehensive Security with Barracuda & ALG Data Guard.
          </h2>
          <p className="bc-cta__sub">Whether you need email phishing protection, ransomware-proof backup, next-gen firewall replacement, cloud security visibility, or 24/7 managed security operations — ALG Data Guard delivers the complete Barracuda platform with expert deployment and ongoing management.</p>
          <div className="bc-cta__actions">
            <button className="bc-btn bc-btn--blue" onClick={() => navigate("/contact")}>
              Book Security Assessment <ArrowIcon />
            </button>
            <button className="bc-btn bc-btn--ghost" onClick={() => navigate("/contact")}>
              Request Platform Demo
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
